import React, { useState, useEffect } from 'react';
import { accountService } from '../../../../apiServices/accountService';
import { formatCurrencyWithSymbol } from '../../../../utils/currencyUtils';
import './TotalCapital.css';

interface TotalCapitalProps {
  accountsCount: number;
}

const TotalCapital: React.FC<TotalCapitalProps> = ({ accountsCount }) => {
  const [actualTotal, setActualTotal] = useState<number | null>(null);
  const [potentialTotal, setPotentialTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    const fetchTotalCapital = async () => {
      if (accountsCount === 0) {
        setActualTotal(0);
        setPotentialTotal(0);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Отримуємо загальний капітал в USD з бекенду
        const response = await accountService.getTotalCapitalInUsd();
        
        setActualTotal(response.actualTotal);
        setPotentialTotal(response.potentialTotal);
        setLastUpdated(new Date());
      } catch (err: any) {
        console.error('Error fetching total capital:', err);
        setError('Не вдалося завантажити загальний капітал');
        setActualTotal(null);
        setPotentialTotal(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTotalCapital();
  }, [accountsCount]);

  if (loading && actualTotal === null) {
    return (
      <div className="total-capital-card card">
        <div className="total-capital-loading">
          <div className="spinner-small"></div>
          <span>Завантаження курсу валют...</span>
        </div>
      </div>
    );
  }

  const totalWithPotential = (actualTotal || 0) + (potentialTotal || 0);

  return (
    <div className="total-capital-card card">
      <div className="total-capital-header">
        <h3 className="total-capital-title">Загальний капітал</h3>
        {lastUpdated && (
          <span className="total-capital-updated">
            Оновлено: {lastUpdated.toLocaleTimeString('uk-UA', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </span>
        )}
      </div>
      <div className="total-capital-body">
        {error && (
          <div className="total-capital-error">
            <span>⚠️ {error}</span>
          </div>
        )}
        
        {potentialTotal !== null && potentialTotal > 0 ? (
          <div className="total-capital-stats-grid">
            {/* Фактичний капітал */}
            <div className="total-capital-stat total-capital-stat-actual">
              <div className="total-capital-stat-label">Фактичний капітал</div>
              <div className="total-capital-stat-value">
                {actualTotal !== null ? formatCurrencyWithSymbol(actualTotal, 'USD') : '—'}
              </div>
            </div>

            {/* Потенційні кошти */}
            <div className="total-capital-stat total-capital-stat-potential">
              <div className="total-capital-stat-label">Мені повернуть</div>
              <div className="total-capital-stat-value">
                {formatCurrencyWithSymbol(potentialTotal, 'USD')}
              </div>
            </div>

            {/* Загальний капітал з потенційними коштами */}
            <div className="total-capital-stat total-capital-stat-total">
              <div className="total-capital-stat-label">Разом</div>
              <div className="total-capital-stat-value">
                {formatCurrencyWithSymbol(totalWithPotential, 'USD')}
              </div>
            </div>
          </div>
        ) : (
          <div className="total-capital-stats-grid total-capital-stats-single">
            {/* Тільки фактичний капітал */}
            <div className="total-capital-stat total-capital-stat-actual">
              <div className="total-capital-stat-label">Фактичний капітал</div>
              <div className="total-capital-stat-value">
                {actualTotal !== null ? formatCurrencyWithSymbol(actualTotal, 'USD') : '—'}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TotalCapital;

